/**
 * What a markup profile is for: materials, equipment, or both.
 *
 * "Standard equipment" offered as the markup for materials is a mistake the
 * dropdowns used to invite, so a profile states what it applies to and each
 * dropdown offers only what fits.
 *
 * The scope rides on the profile's ranges rather than on the profile row,
 * exactly as `basis` does and for the same reason: the row is real columns and
 * `ranges` is the only Json on it. Every range in one profile carries the same
 * value — it describes the profile, not the band.
 *
 * A profile saved before this existed states nothing, and nothing means BOTH.
 * That is not a default chosen for convenience: those profiles are already
 * selected on saved services, and narrowing them would drop a configured markup
 * out of the list a service is relying on.
 */

import type { MarkupProfile, MarkupProfileScope, MarkupRange } from '@/types/pricebook';

/** The kind of part a dropdown is choosing a markup for. */
export type PartKind = 'MATERIAL' | 'EQUIPMENT';

export const MARKUP_SCOPE_LABELS: Record<MarkupProfileScope, string> = {
  MATERIAL: 'Materials',
  EQUIPMENT: 'Equipment',
  BOTH: 'Both',
};

/** What a saved profile applies to; BOTH for anything that never said. */
export function profileScope(ranges: MarkupRange[] | undefined | null): MarkupProfileScope {
  return ranges?.find((range) => range.appliesTo)?.appliesTo ?? 'BOTH';
}

/** Stamps the profile's scope onto every range, which is where it lives. */
export function rangesWithScope(
  ranges: MarkupRange[],
  appliesTo: MarkupProfileScope,
): MarkupRange[] {
  return ranges.map((range) => ({ ...range, appliesTo }));
}

export function profileAppliesToKind(profile: MarkupProfile, kind: PartKind): boolean {
  const scope = profileScope(profile.ranges);
  return scope === 'BOTH' || scope === kind;
}

/**
 * The profiles a dropdown should offer for one kind of part.
 *
 * `selectedId` is always kept, even when it is out of scope: a service already
 * pointing at a profile that has since been narrowed would otherwise show an
 * empty select, and re-picking would quietly change what it charges.
 */
export function markupProfilesForKind(
  profiles: MarkupProfile[],
  kind: PartKind,
  selectedId?: string | null,
): MarkupProfile[] {
  return profiles.filter(
    (profile) => profile.id === selectedId || profileAppliesToKind(profile, kind),
  );
}
